import React, { useCallback, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  LuScanFace,
  LuLandmark,
  LuClipboardList,
  LuCompass,
} from 'react-icons/lu';
import { useUser } from '../../contexts/UserContext';
import useIsMobile from '../../hooks/useIsMobile';
import { COPY } from '../../contracts/copyMap';
import { ROUTES } from '../../contracts/routes';
import AurumButton from '../oracle/AurumButton';
import BoneButton from '../oracle/BoneButton';
import GhostButton from '../oracle/GhostButton';

// First-run interactive tutorial. A self-gating modal overlay shown ONCE to any
// onboarded user who hasn't seen it yet, the moment they land in the app shell.
//
//   onboarding_complete && tutorial_complete !== true   ──> show overlay
//   tutorial_complete === true                          ──> render nothing
//
// `!== true` (not `=== false`) is deliberate: the whole site was revamped, so
// EXISTING users (whose docs predate this field → tutorial_complete undefined)
// should also get the tour, alongside brand-new docs that start the flag false.
// Only an explicit `true` — written when a user finishes or skips — suppresses
// it. No migration needed; the field is backfilled to true on first dismissal.
//
// Three short steps, scan-first. Both SKIP and the final CTA persist the flag;
// FINISH also drops the user into the scanner (the priority action). Mounted
// once in AppLayout so it layers over whichever surface the user lands on.
//
//   ┌───────────────────────────── SKIP ─┐
//   │ • • •                              │  ← step dots
//   │ [icon]                             │
//   │ Headline (serif)                   │
//   │ Body (italic)                      │
//   │ {step body}                        │
//   │ [BACK]            [ NEXT / LAUNCH ] │
//   └────────────────────────────────────┘

const SECTIONS = [
  {
    icon: LuLandmark,
    labelKey: 'TUTORIAL_SECTION_VAULT_LABEL',
    descKey: 'TUTORIAL_SECTION_VAULT_DESC',
  },
  {
    icon: LuClipboardList,
    labelKey: 'TUTORIAL_SECTION_ROUTINE_LABEL',
    descKey: 'TUTORIAL_SECTION_ROUTINE_DESC',
  },
  {
    icon: LuCompass,
    labelKey: 'TUTORIAL_SECTION_EXPLORE_LABEL',
    descKey: 'TUTORIAL_SECTION_EXPLORE_DESC',
  },
];

const STEP_COUNT = 3;

const StepDots = ({ stepIndex }) => (
  <div className="flex items-center gap-2" aria-hidden="true">
    {Array.from({ length: STEP_COUNT }).map((_, i) => (
      <span
        key={i}
        className={[
          'h-1 rounded-full transition-all duration-300',
          i === stepIndex ? 'w-7 bg-aurum' : 'w-3.5 bg-vein',
        ].join(' ')}
      />
    ))}
  </div>
);

// A round Aurum-ringed glyph tile — the editorial icon treatment used across
// the app's empty states and hero blocks.
const GlyphTile = ({ icon: Icon }) => (
  <span
    className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[rgba(201,164,92,0.5)] bg-aurum-faint text-aurum"
    aria-hidden="true"
  >
    <Icon size={22} strokeWidth={1.5} />
  </span>
);

const TutorialOverlay = () => {
  // Tolerate a missing provider (some isolated page tests render the app shell
  // without UserContext). In the real app the provider is always present.
  const { userData, updateUserData } = useUser() || {};
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [stepIndex, setStepIndex] = useState(0);
  // Optimistic local close so dismissal is instant; the Firestore write (and the
  // snapshot that follows) makes it permanent across sessions.
  const [dismissed, setDismissed] = useState(false);

  // Persist completion. Fire-and-forget: a failed write just means the tour
  // re-appears next session, which is harmless (never blocks the user).
  const persistComplete = useCallback(() => {
    setDismissed(true);
    Promise.resolve(
      updateUserData({
        tutorial_complete: true,
        tutorial_completed_at: new Date().toISOString(),
      }),
    ).catch(() => {});
  }, [updateUserData]);

  const handleSkip = useCallback(() => {
    persistComplete();
  }, [persistComplete]);

  const handleBack = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const isLastStep = stepIndex === STEP_COUNT - 1;

  const handleNext = useCallback(() => {
    if (isLastStep) {
      persistComplete();
      navigate(ROUTES.CREATE); // drop into the scanner — the priority action
    } else {
      setStepIndex((i) => i + 1);
    }
  }, [isLastStep, persistComplete, navigate]);

  // Any onboarded user who hasn't explicitly completed/skipped the tour sees it
  // (includes existing users whose docs predate the field → undefined).
  const eligible =
    !!userData &&
    userData.onboarding_complete === true &&
    userData.tutorial_complete !== true;

  if (!eligible || dismissed) return null;

  const navHint = isMobile
    ? COPY.TUTORIAL_SECTIONS_NAV_HINT_MOBILE
    : COPY.TUTORIAL_SECTIONS_NAV_HINT_DESKTOP;

  const steps = [
    {
      icon: LuScanFace,
      headline: COPY.TUTORIAL_SCAN_HEADLINE,
      body: COPY.TUTORIAL_SCAN_BODY,
    },
    {
      icon: LuCompass,
      headline: COPY.TUTORIAL_SECTIONS_HEADLINE,
      body: navHint,
      sections: true,
    },
    {
      icon: LuScanFace,
      headline: COPY.TUTORIAL_LAUNCH_HEADLINE,
      body: COPY.TUTORIAL_LAUNCH_BODY,
    },
  ];
  const step = steps[stepIndex];

  return (
    <motion.div
      className="fixed inset-0 z-[90] flex items-center justify-center px-5"
      style={{ background: 'rgba(0,0,0,0.82)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.24 }}
      role="dialog"
      aria-modal="true"
      aria-label="MOGGED tutorial"
    >
        <motion.section
          className="relative w-full max-w-[460px] rounded-[22px] border border-hairline bg-[rgba(13,13,12,0.92)] p-7 md:p-9 shadow-card-lift overflow-hidden"
          initial={{ scale: 0.9, y: 12 }}
          animate={{ scale: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 320, damping: 26 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Skip — always available, top-right. */}
          <div className="absolute top-3.5 right-3.5">
            <GhostButton onClick={handleSkip} data-testid="tutorial-skip">
              {COPY.TUTORIAL_SKIP_CTA}
            </GhostButton>
          </div>

          <p className="font-display uppercase tracking-[0.22em] text-[11px] text-aurum m-0">
            {COPY.TUTORIAL_EYEBROW}
          </p>

          <div className="mt-4">
            <StepDots stepIndex={stepIndex} />
          </div>

          <motion.div
              key={stepIndex}
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.24, ease: [0.65, 0, 0.35, 1] }}
            >
              <div className="mt-6">
                <GlyphTile icon={step.icon} />
              </div>

              <h2
                className="mt-4 font-display text-bone m-0"
                style={{ fontSize: '32px', lineHeight: 1.12 }}
              >
                {step.headline}
              </h2>
              <p className="mt-3 font-display italic text-bone-dim text-[15px] leading-relaxed m-0">
                {step.body}
              </p>

              {step.sections && (
                <ul className="mt-5 space-y-2.5 list-none p-0 m-0">
                  {SECTIONS.map(({ icon: Icon, labelKey, descKey }) => (
                    <li
                      key={labelKey}
                      className="flex items-center gap-3.5 rounded-field border border-hairline-soft bg-card-sheen px-4 py-3"
                    >
                      <span
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-thumb border border-hairline text-bone"
                        aria-hidden="true"
                      >
                        <Icon size={17} strokeWidth={1.5} />
                      </span>
                      <span className="min-w-0">
                        <span className="block font-display uppercase tracking-[0.16em] text-[12px] text-bone">
                          {COPY[labelKey]}
                        </span>
                        <span className="block font-display italic text-[13px] text-bone-dim">
                          {COPY[descKey]}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>

          <div className="mt-8 flex items-center gap-3">
            {stepIndex > 0 && (
              <BoneButton onClick={handleBack} size="lg" data-testid="tutorial-back">
                {COPY.TUTORIAL_BACK_CTA}
              </BoneButton>
            )}
            <AurumButton
              variant="metallic"
              size="lg"
              fullWidth
              onClick={handleNext}
              trailingIcon={<span aria-hidden="true">&rarr;</span>}
              className="flex-1"
              data-testid="tutorial-next"
            >
              {isLastStep ? COPY.TUTORIAL_LAUNCH_CTA : COPY.TUTORIAL_NEXT_CTA}
            </AurumButton>
          </div>
        </motion.section>
    </motion.div>
  );
};

export default TutorialOverlay;
